import { useState } from "react";
import { Upload, Loader2, CheckCircle2, AlertTriangle, XCircle, Image as ImageIcon } from "lucide-react";
import { validatePhoto, fileToBase64 } from "../lib/questionnaire";
import { toast } from "sonner";

/**
 * Reusable photo upload card with live validation feedback.
 *
 * Props:
 *  - label: title above the upload area
 *  - subLabel: subtitle
 *  - onValid(result): called once validation succeeds — result has { image_base64, phash, face_crop_base64, confidence }
 *  - onClear(): called when user removes the photo
 *  - compareAgainstPhash: optional phash to duplicate-check against
 *  - testIdPrefix: for data-testid naming
 */
export default function PhotoUpload({
    label,
    subLabel,
    onValid,
    onClear,
    compareAgainstPhash = null,
    testIdPrefix = "photo",
}) {
    const [status, setStatus] = useState("idle"); // idle | validating | ok | error
    const [result, setResult] = useState(null);
    const [preview, setPreview] = useState("");
    const [errors, setErrors] = useState([]);
    const [warnings, setWarnings] = useState([]);

    async function handleFile(file) {
        if (!file) return;
        if (!["image/jpeg", "image/png"].includes(file.type)) {
            toast.error("Only JPG and PNG are supported.");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Photo is larger than 5 MB. Please compress or resize.");
            return;
        }
        setStatus("validating");
        setErrors([]);
        setWarnings([]);
        setResult(null);
        try {
            const image_base64 = await fileToBase64(file);
            setPreview(URL.createObjectURL(file));
            const res = await validatePhoto({ image_base64, compare_against_phash: compareAgainstPhash });
            setErrors(res.errors || []);
            setWarnings(res.warnings || []);
            if (res.ok) {
                setStatus("ok");
                const payload = {
                    image_base64,
                    phash: res.phash,
                    face_crop_base64: res.face_crop_base64,
                    confidence: res.confidence,
                    skin_tone: res.skin_tone || null,
                };
                setResult(payload);
                onValid?.(payload);
            } else {
                setStatus("error");
            }
        } catch (err) {
            setStatus("error");
            setErrors([{ code: "network", message: err?.response?.data?.detail || "Upload failed. Please try again." }]);
        }
    }

    function clear() {
        setStatus("idle");
        setResult(null);
        setErrors([]);
        setWarnings([]);
        if (preview) URL.revokeObjectURL(preview);
        setPreview("");
        onClear?.();
    }

    return (
        <div className="space-y-5">
            <div className="rounded-[24px] bg-white p-6 shadow-soft md:p-8" data-testid={`${testIdPrefix}-card`}>
                <div className="flex items-start gap-3">
                    <ImageIcon className="mt-0.5 h-4 w-4 flex-none text-gold" />
                    <div>
                        <div className="font-serif text-lg text-charcoal">{label}</div>
                        {subLabel && <p className="mt-1 text-sm text-ink">{subLabel}</p>}
                    </div>
                </div>

                <label
                    className="mt-6 flex cursor-pointer flex-col items-center justify-center rounded-[20px] border border-dashed border-charcoal/20 bg-ivory/60 px-6 py-10 text-center transition hover:border-gold hover:bg-champagne/40"
                    data-testid={`${testIdPrefix}-dropzone`}
                >
                    <input
                        type="file"
                        accept="image/jpeg,image/png"
                        className="sr-only"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid={`${testIdPrefix}-input`}
                    />
                    {status === "validating" ? (
                        <>
                            <Loader2 className="h-6 w-6 animate-spin text-charcoal/60" />
                            <p className="mt-3 text-sm text-ink">Checking your photo…</p>
                        </>
                    ) : preview ? (
                        <img src={preview} alt="preview" className="h-56 w-auto rounded-[14px] object-cover shadow-soft" />
                    ) : (
                        <>
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-champagne text-charcoal">
                                <Upload className="h-5 w-5" />
                            </span>
                            <div className="mt-5 font-serif text-lg text-charcoal">Tap to upload</div>
                            <p className="mt-1.5 text-xs text-ink/70">JPG or PNG · up to 5 MB · min 300×300</p>
                        </>
                    )}
                </label>

                {/* Face preview chip */}
                {status === "ok" && result?.face_crop_base64 && (
                    <div className="mt-5 flex items-center gap-4 rounded-[16px] bg-champagne/50 p-4" data-testid={`${testIdPrefix}-ok`}>
                        <img
                            src={`data:image/jpeg;base64,${result.face_crop_base64}`}
                            alt="Face preview"
                            className="h-16 w-16 rounded-xl object-cover shadow-soft"
                        />
                        <div className="flex-1">
                            <div className="flex items-center gap-2 text-charcoal">
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                <span className="text-sm font-medium">Photo looks good</span>
                            </div>
                            <p className="mt-1 text-xs text-ink">
                                Confidence · {result.confidence}/100
                                {result.skin_tone?.status === "ok" && (
                                    <>
                                        {" · "}
                                        <span className="capitalize">{String(result.skin_tone.tone || "").replace("_", " ")}</span>
                                        {" "}
                                        <span className="capitalize">{result.skin_tone.undertone}</span>
                                        {" undertone"}
                                    </>
                                )}
                            </p>
                        </div>
                        {result.skin_tone?.dominant_hex && (
                            <span
                                className="inline-block h-8 w-8 flex-none rounded-lg ring-1 ring-black/10"
                                style={{ background: result.skin_tone.dominant_hex }}
                                title={result.skin_tone.dominant_hex}
                            />
                        )}
                        <button
                            type="button"
                            onClick={clear}
                            className="text-xs text-ink underline-offset-4 hover:underline"
                            data-testid={`${testIdPrefix}-remove`}
                        >
                            Replace
                        </button>
                    </div>
                )}

                {status === "error" && errors.length > 0 && (
                    <div className="mt-5 space-y-3" data-testid={`${testIdPrefix}-error`}>
                        {errors.map((e) => (
                            <div key={e.code} className="flex items-start gap-3 rounded-[16px] bg-rose-50 p-4 ring-1 ring-rose-100">
                                <XCircle className="mt-0.5 h-4 w-4 flex-none text-rose-500" />
                                <div className="text-sm text-charcoal">
                                    <div className="font-medium">{e.message}</div>
                                    <div className="mt-1 text-xs text-ink/80">Why this matters: accurate analysis needs a clear, neutral photo.</div>
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={clear}
                            className="text-xs text-charcoal underline underline-offset-4"
                            data-testid={`${testIdPrefix}-try-again`}
                        >
                            Try another photo
                        </button>
                    </div>
                )}

                {warnings.length > 0 && (
                    <div className="mt-4 space-y-2">
                        {warnings.map((w) => (
                            <div key={w.code} className="flex items-start gap-3 rounded-[14px] bg-amber-50 p-3 ring-1 ring-amber-100">
                                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
                                <span className="text-xs text-charcoal">{w.message}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
